/** Help panel: ranked list of all LaTeX commands used in the workspace
 *  with good/warning/bad/neutral assessment and descriptions.
 */

import { useEffect, useMemo, useState } from "react";
import { FilesService } from "../services";
import type { LatexCommandInfo, LatexCommandsReport } from "../types";

type Filter = "all" | "good" | "warning" | "bad";

const CATEGORY_LABELS: Record<string, string> = {
  good: "Good",
  warning: "Warning",
  bad: "Problematic",
  neutral: "Neutral",
};

export function HelpPanel(): JSX.Element {
  const [report, setReport] = useState<LatexCommandsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError("");
    try {
      const r = await FilesService.latexCommands();
      setReport(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load commands");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const counts = useMemo(() => {
    if (!report) return { good: 0, warning: 0, bad: 0, neutral: 0 };
    const c = { good: 0, warning: 0, bad: 0, neutral: 0 };
    for (const cmd of report.commands) {
      c[cmd.category as keyof typeof c] =
        (c[cmd.category as keyof typeof c] || 0) + 1;
    }
    return c;
  }, [report]);

  const filtered = useMemo(() => {
    if (!report) return [];
    return report.commands.filter((cmd) => {
      if (filter !== "all" && cmd.category !== filter) return false;
      if (search && !cmd.command.toLowerCase().includes(search.toLowerCase()))
        return false;
      return true;
    });
  }, [report, filter, search]);

  if (loading) {
    return <div className="empty">Analyzing LaTeX commands...</div>;
  }
  if (error) {
    return (
      <div>
        <div className="empty" style={{ color: "var(--error)" }}>{error}</div>
        <button onClick={() => void load()}>Retry</button>
      </div>
    );
  }
  if (!report) {
    return <div className="empty">No data.</div>;
  }

  return (
    <div className="help-panel">
      {/* Summary */}
      <div className="help-summary">
        <span>
          <strong>{report.total_commands}</strong> total uses across{" "}
          <strong>{report.unique_commands}</strong> unique commands in{" "}
          <strong>{report.total_tex_files}</strong> .tex files
        </span>
        <button onClick={() => void load()} style={{ marginLeft: 8 }}>
          ↻ Refresh
        </button>
      </div>

      {/* Category badges */}
      <div className="help-filters">
        <button
          className={filter === "all" ? "primary" : ""}
          onClick={() => setFilter("all")}
        >
          All ({report.commands.length})
        </button>
        <button
          className={filter === "good" ? "primary" : ""}
          onClick={() => setFilter("good")}
        >
          ✓ Good ({counts.good})
        </button>
        <button
          className={filter === "warning" ? "primary" : ""}
          onClick={() => setFilter("warning")}
        >
          ⚠ Warning ({counts.warning})
        </button>
        <button
          className={filter === "bad" ? "primary" : ""}
          onClick={() => setFilter("bad")}
        >
          ✗ Problematic ({counts.bad})
        </button>
        <input
          className="help-search"
          placeholder="Search commands..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginLeft: "auto", width: 180 }}
        />
      </div>

      {/* Command table */}
      <div className="help-table scroll">
        <div className="help-row help-header">
          <span className="help-col-cmd">Command</span>
          <span className="help-col-count">Count</span>
          <span className="help-col-cat">Status</span>
          <span className="help-col-desc">Description</span>
        </div>
        {filtered.length === 0 && (
          <div className="empty">No commands match the filter.</div>
        )}
        {filtered.map((cmd: LatexCommandInfo) => (
          <div
            key={cmd.command}
            className={`help-row help-row-${cmd.category}`}
            onClick={() =>
              setExpanded((e) => (e === cmd.command ? null : cmd.command))
            }
          >
            <span className="help-col-cmd">
              <code>{cmd.command}</code>
            </span>
            <span className="help-col-count">{cmd.count}</span>
            <span className={`help-col-cat cat-${cmd.category}`}>
              {CATEGORY_LABELS[cmd.category]}
            </span>
            <span className="help-col-desc">
              {expanded === cmd.command ? (
                <span>
                  {cmd.description}
                  {cmd.files.length > 0 && (
                    <div className="help-files">
                      <strong>Used in:</strong> {cmd.files.join(", ")}
                    </div>
                  )}
                </span>
              ) : (
                <span className="help-desc-truncate">
                  {cmd.description}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
